// Cohort misconception-clustering run detail — one grouping pass over a class's
// solution-analysis observations. Reads the DEDICATED `cohort-cluster-runs`
// record (the OperationRun), then its model runs.
//
// The left card is a coverage panel rather than the generic context block. Every
// other operation's failure is visible as an error; this one's is a green run
// that grouped almost nothing, so the counters that expose it — how much of the
// corpus the kept clusters actually cover, how many groups were discarded, and
// whether the model referenced ids that were never supplied — are given
// first-class space.
//
// Mirrors MarkAttemptDetail using the shared operation-detail components.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
    formatCost,
    totalRunCost,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

/** Below this, a completed run told the teacher almost nothing. */
const LOW_COVERAGE = 0.25;

export function CohortClusterDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const { data: runData, isLoading, error } = useQuery({
        queryKey: ['cohortClusterRun', id],
        queryFn: () => adminApi.getCohortClusterRun(id!),
        enabled: !!id,
    });
    const run = runData?.run;

    // Clustering ModelOutputs are saved against the cohort id (submissionId).
    const cohortId = run?.cohortId;
    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', cohortId],
        queryFn: () => adminApi.getModelRuns(cohortId!),
        enabled: !!cohortId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find((r) => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=cohortcluster');

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading clustering run…</div></div>;
    }
    if (error || !run) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading clustering run: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Misconception Clustering</button>
            </div>
        );
    }

    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Clustering Detail' },
    });
    const selectedRun = runs.find((r) => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRawResponse));

    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRawResponse}
        />
    ) : undefined;

    const o = run.outcome;
    const lowCoverage = run.coverage !== undefined && run.coverage < LOW_COVERAGE;
    const droppedGroups = o ? o.groupsReturned - o.clustersKept : 0;
    const inventedIds = o ? o.unknownIds + o.duplicateIds : 0;

    return (
        <OperationDetailPage
            title="Misconception Clustering Details"
            id={run.id}
            backLabel="Misconception Clustering"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Cluster" />}
            onBack={goBack}
            context={
                <ContextGrid
                    left={
                        <MetadataCard title="Clustering">
                            <MetaItem label="Teacher">
                                {run.teacherName || run.userId}
                                {run.teacherEmail && <span className="muted"> · {run.teacherEmail}</span>}
                            </MetaItem>
                            <MetaItem label="Cohort">
                                <span className="datetime" style={{ fontFamily: 'monospace' }}>{run.cohortId}</span>
                                <span className="muted"> · {run.subject}</span>
                            </MetaItem>

                            {o && (
                                <MetaItem label="Coverage">
                                    {run.coverage === undefined ? (
                                        <span className="muted">Nothing was sent to the model.</span>
                                    ) : (
                                        <>
                                            <span className={`tag ${lowCoverage ? 'status-failed' : ''}`}>
                                                {Math.round(run.coverage * 100)}% · {o.observationsClustered} of {o.observationsSent}
                                            </span>
                                            {lowCoverage && (
                                                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                                    This run succeeded while grouping almost nothing — the teacher
                                                    saw &ldquo;your class shares no misconceptions&rdquo;, which is
                                                    indistinguishable from a healthy class.
                                                </div>
                                            )}
                                        </>
                                    )}
                                </MetaItem>
                            )}

                            {o && (
                                <MetaItem label="Clusters">
                                    <strong>{o.clustersKept}</strong> kept of {o.groupsReturned} returned
                                    {droppedGroups > 0 && (
                                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                            {droppedGroups} discarded — a group is dropped when fewer than two
                                            distinct students share it, which is intended, not a fault.
                                        </div>
                                    )}
                                </MetaItem>
                            )}

                            {!!inventedIds && (
                                <MetaItem label="Invented references">
                                    <span className="tag status-failed">
                                        {o!.unknownIds} unknown · {o!.duplicateIds} re-used
                                    </span>
                                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                        The model cited observation ids it was never given, or placed one in two
                                        groups. These were discarded before any teacher saw them — but it points
                                        at the prompt or the schema, not at the data.
                                    </div>
                                </MetaItem>
                            )}

                            {o && !inventedIds && (
                                <MetaItem label="Reference integrity">
                                    <span className="muted">Every id the model returned was one it was given.</span>
                                </MetaItem>
                            )}
                        </MetadataCard>
                    }
                    right={
                        <MetadataCard title="Run">
                            <MetaItem label="Status"><span className={`tag status-${run.status}`}>{run.status}</span></MetaItem>
                            <MetaItem label="Latency">{formatLatency(run.latencyMs)}</MetaItem>
                            <MetaItem label="Started">
                                <span className="datetime">{format(new Date(run.startedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            {run.completedAt && (
                                <MetaItem label="Completed">
                                    <span className="datetime">{format(new Date(run.completedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            )}
                            {run.llm && (
                                <MetaItem label="Tokens / Cost">
                                    {run.llm.inputTokens.toLocaleString()} in / {run.llm.outputTokens.toLocaleString()} out
                                    {/* Every call of the operation (the runs below), not the record's own figure. */}
                                    {(totalRunCost(runs) ?? run.llm.costUsd) != null && <span className="muted"> · {formatCost(totalRunCost(runs) ?? run.llm.costUsd!)}</span>}
                                </MetaItem>
                            )}
                            {run.error && (
                                <MetaItem label="Error"><span className="tag status-failed">{run.error}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
            }
            runs={runs.length > 0 ? (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={(r) => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={(r) => setSelectedRunId(r.id)}
                    getRowClass={(r) => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            ) : (
                <div className="card"><p className="muted">No model runs recorded for this clustering pass.</p></div>
            )}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
    );
}
